import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { Alert, Badge, Button, Card, Modal, Spinner } from "react-bootstrap";

import { db } from "../../../../shared/libs/firebaseConfig";
import {
  AFFILIATE_APPLICATION_STATUSES,
  AFFILIATE_COLLECTIONS,
  AFFILIATE_STATUSES,
} from "../../../../shared/utils/affiliateSchema";

function maskAccount(value = "") {
  const raw = String(value || "").replace(/\D/g, "");
  if (!raw) return "-";
  if (raw.length <= 6) return raw;
  return `${raw.slice(0, 3)}${"*".repeat(Math.max(0, raw.length - 7))}${raw.slice(-4)}`;
}

function makeAffiliateCode() {
  return `CC${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

async function generateUniqueAffiliateCode() {
  for (let i = 0; i < 12; i += 1) {
    const code = makeAffiliateCode();
    const snap = await getDoc(doc(db, AFFILIATE_COLLECTIONS.codes, code));
    if (!snap.exists()) return code;
  }
  throw new Error("ไม่สามารถสร้าง affiliate code ที่ไม่ซ้ำได้");
}

export default function AdminAffiliateApplications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [qrPreview, setQrPreview] = useState(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError("");
      const snap = await getDocs(
        query(
          collection(db, AFFILIATE_COLLECTIONS.applications),
          where("status", "==", AFFILIATE_APPLICATION_STATUSES.pending)
        )
      );
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a?.createdAt?.toMillis?.() || 0;
        const tb = b?.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setItems(rows);
    } catch (e) {
      console.error(e);
      setError(e?.message || "โหลดคำขอ Affiliate ไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const approveApplication = async (item) => {
    if (!item?.id || !item?.userId) return;
    try {
      setUpdatingId(item.id);
      setError("");
      setSuccess("");

      const code = await generateUniqueAffiliateCode();
      const batch = writeBatch(db);
      const appRef = doc(db, AFFILIATE_COLLECTIONS.applications, item.id);
      const affiliateRef = doc(db, AFFILIATE_COLLECTIONS.affiliates, item.userId);
      const codeRef = doc(db, AFFILIATE_COLLECTIONS.codes, code);

      batch.set(
        affiliateRef,
        {
          userId: item.userId,
          code,
          status: AFFILIATE_STATUSES.approved,
          profile: {
            displayName: item?.profile?.fullName || "",
            publicBio: "",
          },
          contact: {
            email: item?.contact?.email || "",
            phoneNumber: item?.profile?.phoneNumber || "",
            lineId: "",
          },
          bankAccount: {
            bankName: item?.bankAccount?.bankName || "",
            accountName: item?.bankAccount?.accountName || "",
            accountNumber: String(item?.bankAccount?.accountNumber || "").trim(),
            accountNumberLast4: String(item?.bankAccount?.accountNumber || "").replace(/\D/g, "").slice(-4),
          },
          qrCode: item?.qrCode || {
            imageUrl: "",
            storagePath: "",
            updatedAt: null,
          },
          applicationId: item.id,
          approvedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      batch.set(codeRef, {
        code,
        affiliateId: item.userId,
        affiliateUserId: item.userId,
        status: AFFILIATE_STATUSES.approved,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      batch.update(appRef, {
        status: AFFILIATE_APPLICATION_STATUSES.approved,
        affiliateCode: code,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await batch.commit();
      setSuccess(`อนุมัติ Affiliate และสร้าง code ${code} แล้ว`);
      await loadItems();
    } catch (e) {
      console.error(e);
      setError(e?.message || "อนุมัติคำขอไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  };

  const rejectApplication = async (item) => {
    if (!item?.id) return;
    try {
      setUpdatingId(item.id);
      setError("");
      setSuccess("");
      await updateDoc(doc(db, AFFILIATE_COLLECTIONS.applications, item.id), {
        status: AFFILIATE_APPLICATION_STATUSES.rejected,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSuccess("ปฏิเสธคำขอเรียบร้อยแล้ว");
      await loadItems();
    } catch (e) {
      console.error(e);
      setError(e?.message || "ปฏิเสธคำขอไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  };

  const pendingCount = useMemo(() => items.length, [items]);

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-0">Affiliate Applications</h4>
          <div className="text-muted small">คำขอรอตรวจสอบ {pendingCount} รายการ</div>
        </div>
        <Button variant="outline-secondary" onClick={loadItems} disabled={loading || !!updatingId}>
          รีเฟรช
        </Button>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <div className="py-4 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลด...
        </div>
      ) : items.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-muted py-5">ไม่มีคำขอที่รอตรวจสอบ</Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {items.map((item) => (
            <Card key={item.id} className="border">
              <Card.Body>
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <div>
                    <div className="fw-semibold">{item?.profile?.fullName || "-"}</div>
                    <div className="text-muted small">{item?.contact?.email || "-"}</div>
                  </div>
                  <Badge bg="warning" text="dark">{item.status}</Badge>
                </div>

                <div className="row g-3 mt-2">
                  <div className="col-md-6">
                    <div className="small text-muted">ชื่อ-นามสกุล</div>
                    <div>{item?.profile?.fullName || "-"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">เบอร์โทรศัพท์</div>
                    <div>{item?.profile?.phoneNumber || "-"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">อีเมลติดต่อ</div>
                    <div>{item?.contact?.email || "-"}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="small text-muted">สถานะ</div>
                    <div>{item?.status || "-"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">ธนาคาร</div>
                    <div>{item?.bankAccount?.bankName || "-"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">ชื่อบัญชี</div>
                    <div>{item?.bankAccount?.accountName || "-"}</div>
                  </div>
                  <div className="col-md-4">
                    <div className="small text-muted">เลขบัญชี</div>
                    <div>{maskAccount(item?.bankAccount?.accountNumber)}</div>
                  </div>
                  <div className="col-12">
                    <div className="small text-muted">เหตุผลในการสมัคร</div>
                    <div>{item?.message || "-"}</div>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setQrPreview(item)}
                  >
                    QR รับเงิน
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => approveApplication(item)}
                    disabled={!!updatingId}
                  >
                    {updatingId === item.id ? "กำลังอนุมัติ..." : "อนุมัติ"}
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => rejectApplication(item)}
                    disabled={!!updatingId}
                  >
                    ปฏิเสธ
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      <Modal show={!!qrPreview} onHide={() => setQrPreview(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>QR รับเงิน</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrPreview?.qrCode?.imageUrl ? (
            <>
              <img
                src={qrPreview.qrCode.imageUrl}
                alt="QR รับเงิน"
                style={{ maxWidth: "100%", width: 320, borderRadius: 8 }}
              />
              <div className="text-muted small text-break mt-3">
                {qrPreview?.qrCode?.storagePath || "-"}
              </div>
            </>
          ) : (
            <div className="text-muted py-4">ยังไม่มี QR รับเงิน</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setQrPreview(null)}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
